import { useState, useCallback } from "react";

export interface ISelectedCard {
  uuid: string;
  type: "venue" | "service" | "food";
  name?: string;
}

export const useUpsellCardSelection = () => {
  const [selectedCardUuids, setSelectedCardUuids] = useState<ISelectedCard[]>(
    [],
  );

  // Handler for checkbox selection
  const handleCardCheckChange = useCallback(
    (
      uuid: string,
      type: "venue" | "service" | "food",
      checked: boolean,
      name?: string,
    ) => {
      setSelectedCardUuids((prev) => {
        if (checked) {
          // Add if not already present
          const exists = prev.some((item) => item.uuid === uuid);
          if (!exists) {
            return [...prev, { uuid, type, name: name || "" }];
          }
          return prev;
        } else {
          // Remove from selection
          return prev.filter((item) => item.uuid !== uuid);
        }
      });
    },
    [],
  );

  // Check if a card is selected
  const isCardSelected = useCallback(
    (uuid: string) => {
      return selectedCardUuids.some((item) => item.uuid === uuid);
    },
    [selectedCardUuids],
  );

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectedCardUuids([]);
  }, []);

  // Get all selected UUIDs
  const getSelectedUuids = useCallback(() => {
    return selectedCardUuids.map((item) => item.uuid);
  }, [selectedCardUuids]);

  // Select all cards at once
  const selectAll = useCallback(
    (
      cards: {
        uuid: string;
        type: "venue" | "service" | "food";
        name?: string;
      }[],
    ) => {
      setSelectedCardUuids(cards);
    },
    [],
  );

  // Get selected card names
  const getSelectedCardNames = useCallback(() => {
    return selectedCardUuids.map((item) => ({
      name: item.name || "Unnamed",
      type: item.type,
    }));
  }, [selectedCardUuids]);

  // Get count of selected cards
  const selectedCount = selectedCardUuids.length;

  return {
    selectedCardUuids,
    selectedCount,
    handleCardCheckChange,
    isCardSelected,
    clearSelection,
    getSelectedUuids,
    getSelectedCardNames,
    selectAll,
  };
};
